import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    FormControlLabel,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    Checkbox,
    Select,
    MenuItem,
    InputLabel,
} from "@material-ui/core";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { toast } from "react-toastify";
import apiHelper from "../../../util/ApiHelper";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    card: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: "16px",
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: "#333",
    },
    fieldLabel: {
        fontSize: "14px",
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
        color: "#333",
    },
    fieldRow: {
        marginBottom: theme.spacing(2),
    },
    planChip: {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 16px",
        borderRadius: "20px",
        background: "linear-gradient(90deg, #C94B42 0%, #F05128 100%)",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 600,
    },
    cashBadge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 16px",
        borderRadius: "4px",
        background: "#e8f5e9",
        border: "1px solid #a5d6a7",
        color: "#2e7d32",
        fontSize: "14px",
        fontWeight: 500,
    },
    sendCodeBtn: {
        height: "40px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        background: "#306F9D",
        color: "#fff",
        "&:hover": { background: "#245980" },
        "&.Mui-disabled": { background: "#ccc", color: "#fff" },
    },
    verifyBtn: {
        height: "40px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        background: "#555",
        color: "#fff",
        "&:hover": { background: "#333" },
        "&.Mui-disabled": { background: "#ccc", color: "#fff" },
    },
    verifiedChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        color: "#2e7d32",
        fontSize: "13px",
        fontWeight: 500,
    },
    termsBox: {
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: theme.spacing(2),
        maxHeight: "200px",
        overflowY: "auto",
        background: "#f9f9f9",
        fontSize: "13px",
        lineHeight: 1.7,
        color: "#333",
        marginBottom: theme.spacing(1.5),
        "& strong, & b": { fontWeight: "bold" },
        "& em, & i": { fontStyle: "italic" },
        "& u": { textDecoration: "underline" },
        "& h1, & h2, & h3, & h4, & h5, & h6": { fontWeight: "bold", margin: "0.5em 0" },
        "& h1": { fontSize: "1.5em" },
        "& h2": { fontSize: "1.3em" },
        "& h3": { fontSize: "1.1em" },
        "& p": { margin: "0.5em 0" },
        "& ul, & ol": { paddingLeft: "1.5em", margin: "0.5em 0" },
        "& ul": { listStyleType: "disc" },
        "& ol": { listStyleType: "decimal" },
        "& li": { display: "list-item" },
        "& a": { color: "#306F9D", textDecoration: "underline" },
    },
    submitBtn: {
        marginTop: theme.spacing(2),
        padding: "10px 40px",
        fontSize: "15px",
        background: "#306F9D",
        color: "#fff",
        "&:hover": { background: "#245980" },
    },
    langToggle: {
        marginLeft: "auto",
        display: "flex",
        gap: "4px",
    },
    phoneWrapper: {
        "& .react-tel-input .form-control": {
            width: "100%",
            height: "40px",
            fontSize: "14px",
            borderColor: "#ccc",
            borderRadius: "4px",
            paddingLeft: "60px",
        },
        "& .react-tel-input .flag-dropdown": {
            borderColor: "#ccc",
            borderRadius: "4px 0 0 4px",
            background: "#fafafa",
        },
        "& .react-tel-input .form-control:focus": {
            borderColor: "#306F9D",
            boxShadow: "0 0 0 1px #306F9D",
            outline: "none",
        },
    },
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => currentYear - i);


const TRANSLATIONS = {
    en: {
        pageTitle: "New Member Registration",
        sectionPlan: "Plan & Payment",
        labelPlan: "Plan",
        planName: "Firebird",
        labelPayment: "Payment Method",
        paymentMethod: "Cash",
        sectionPersonal: "Personal Information",
        labelPrefix: "Prefix",
        prefixOptions: [
            { value: "0", label: "Mr" },
            { value: "1", label: "Ms" },
            { value: "2", label: "Miss" },
            { value: "3", label: "Prefer not to say" },
        ],
        labelFirstName: "First Name",
        placeholderFirstName: "First name",
        labelLastName: "Last Name",
        placeholderLastName: "Last name",
        labelDob: "Date of Birth",
        labelMonth: "Month",
        labelYear: "Year",
        months: ["January", "February", "March", "April", "May", "June",
                 "July", "August", "September", "October", "November", "December"],
        labelMobile: "Mobile Number",
        sectionLogin: "Login Information",
        labelEmail: "Email",
        placeholderEmail: "email@example.com",
        btnSendCode: "Send Code",
        btnSent: "Sent",
        btnResend: (n) => `Resend (${n}s)`,
        btnSending: "Sending...",
        labelOtp: "Verification Code",
        placeholderOtp: "Enter 6-digit code",
        emailVerified: "Email verified",
        btnVerify: "Verify",
        btnVerifying: "Verifying...",
        labelPassword: "Password",
        placeholderPassword: "Password",
        labelConfirmPassword: "Confirm Password",
        placeholderConfirmPassword: "Confirm password",
        sectionTnc: "Terms & Conditions",
        tncText: "",
        labelAgree: "I acknowledge that I have read and confirmed that I understand and accept the CineClub Terms and Conditions set out above.",
        labelNoPromo: "I do not want to receive HKIFFS promotional email.",
        labelConfirmToken: "Confirm Token (For Staff Use)",
        placeholderConfirmToken: "Enter confirm token",
        btnReset: "Reset",
        btnRegister: "Register Member",
        btnRegistering: "Registering...",
        errRequired: "Required",
        errEmailInvalid: "Invalid email address",
        errEmailVerify: "Email must be verified",
        errPasswordMatch: "Passwords do not match",
        errAgreeTerms: "You must accept the terms",
        errEnterEmail: "Please enter an email first",
        errEnterOtp: "Please enter the verification code",
        errFillRequired: "Please fill in all required fields.",
        toastCodeSent: "Verification code sent.",
        toastSendFailed: "Failed to send verification code.",
        toastVerifyFailed: "Invalid verification code.",
        toastEmailVerified: "Email verified.",
        toastSuccess: "Member registered successfully.",
        toastFailed: "Failed to register member.",
        langValue: "en",
    },
    zhHant: {
        pageTitle: "新會員登記",
        sectionPlan: "計劃及付款",
        labelPlan: "計劃",
        planName: "Firebird",
        labelPayment: "付款方式",
        paymentMethod: "現金",
        sectionPersonal: "個人資料",
        labelPrefix: "稱謂",
        prefixOptions: [
            { value: "0", label: "先生" },
            { value: "1", label: "女士" },
            { value: "2", label: "小姐" },
            { value: "3", label: "不欲透露" },
        ],
        labelFirstName: "名字",
        placeholderFirstName: "名字",
        labelLastName: "姓氏",
        placeholderLastName: "姓氏",
        labelDob: "出生日期",
        labelMonth: "月份",
        labelYear: "年份",
        months: ["一月", "二月", "三月", "四月", "五月", "六月",
                 "七月", "八月", "九月", "十月", "十一月", "十二月"],
        labelMobile: "手提電話號碼",
        sectionLogin: "登入資料",
        labelEmail: "電郵地址",
        placeholderEmail: "email@example.com",
        btnSendCode: "發送驗證碼",
        btnSent: "已發送",
        btnResend: (n) => `重新發送 (${n}秒)`,
        btnSending: "發送中...",
        labelOtp: "驗證碼",
        placeholderOtp: "輸入6位驗證碼",
        emailVerified: "電郵已驗證",
        btnVerify: "驗證",
        btnVerifying: "驗證中...",
        labelPassword: "密碼",
        placeholderPassword: "密碼",
        labelConfirmPassword: "確認密碼",
        placeholderConfirmPassword: "確認密碼",
        sectionTnc: "條款及細則",
        tncText: "",
        labelAgree: "本人同意接受上述CineClub入會條款及細則",
        labelNoPromo: "我不願意接收有關香港國際電影節協會活動及推廣的電子通訊",
        labelConfirmToken: "確認碼（此欄由職員填寫）",
        placeholderConfirmToken: "輸入確認碼",
        btnReset: "重設",
        btnRegister: "登記會員",
        btnRegistering: "登記中...",
        errRequired: "必填",
        errEmailInvalid: "電郵地址格式不正確",
        errEmailVerify: "電郵必須經過驗證",
        errPasswordMatch: "密碼不相符",
        errAgreeTerms: "您必須接受條款",
        errEnterEmail: "請先輸入電郵地址",
        errEnterOtp: "請輸入驗證碼",
        errFillRequired: "請填寫所有必填欄位。",
        toastCodeSent: "驗證碼已發送。",
        toastSendFailed: "驗證碼發送失敗。",
        toastVerifyFailed: "驗證碼無效。",
        toastEmailVerified: "電郵已驗證。",
        toastSuccess: "會員登記成功。",
        toastFailed: "會員登記失敗。",
        langValue: "tc",
    },
};

const defaultForm = {
    gender: "",
    firstName: "",
    lastName: "",
    dobMonth: "",
    dobYear: "",
    contactNumber: "",
    email: "",
    otpCode: "",
    password: "",
    confirmPassword: "",
    confirmToken: "",
    termsAccepted: false,
    receivePromotion: true,
};

const isSuccessResponse = (res) => res?.status === 200 || res?.status === 201;

const getGenderLabel = (genderValue) =>
    TRANSLATIONS.en.prefixOptions.find((opt) => opt.value === genderValue)?.label;

export default function MemberRegistrationPage({ disabled }) {
    const classes = useStyles();
    const [lang, setLang] = useState("zhHant");
    const t = TRANSLATIONS[lang];

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({});

    const [otpSent, setOtpSent] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [otpCooldown, setOtpCooldown] = useState(0);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [tncData, setTncData] = useState({ contentEn: "", contentTc: "" });

    useEffect(() => {
        apiHelper.cineclub.getMembershipTnc()
            .then((res) => {
                const item = (res?.data || [])[0];
                if (item) setTncData(item);
            })
            .catch(() => {});
    }, []);

    const tncText = lang === "en" ? tncData.contentEn : tncData.contentTc;

    useEffect(() => {
        if (otpCooldown <= 0) return;
        const timer = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [otpCooldown]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
        if (field === "email") {
            setOtpSent(false);
            setEmailVerified(false);
            setOtpCooldown(0);
        }
    };

    const handleSendCode = async () => {
        if (!form.email.trim()) {
            setErrors((prev) => ({ ...prev, email: t.errEnterEmail }));
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            setErrors((prev) => ({ ...prev, email: t.errEmailInvalid }));
            return;
        }
        setSendingOtp(true);
        try {
            const res = await apiHelper.cineclub.sendMembershipEmailOtp(form.email.trim());
            if (!isSuccessResponse(res)) {
                return;
            }
            setOtpSent(true);
            setOtpCooldown(60);
            toast.dark(t.toastCodeSent);
        } catch (err) {
            toast.error(err?.response?.data?.message || t.toastSendFailed);
        } finally {
            setSendingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!form.otpCode.trim()) {
            setErrors((prev) => ({ ...prev, otpCode: t.errEnterOtp }));
            return;
        }
        setVerifyingOtp(true);
        try {
            const res = await apiHelper.cineclub.verifyMembershipEmailOtp(form.email.trim(), form.otpCode.trim());
            if (!isSuccessResponse(res)) {
                setErrors((prev) => ({ ...prev, otpCode: res?.data?.message || t.toastVerifyFailed }));
                return;
            }
            setEmailVerified(true);
            toast.dark(t.toastEmailVerified);
        } catch (err) {
            const msg = err?.response?.data?.message || t.toastVerifyFailed;
            setErrors((prev) => ({ ...prev, otpCode: msg }));
            toast.error(msg);
        } finally {
            setVerifyingOtp(false);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.gender) newErrors.gender = t.errRequired;
        if (!form.firstName.trim()) newErrors.firstName = t.errRequired;
        if (!form.lastName.trim()) newErrors.lastName = t.errRequired;
        if (!form.dobMonth) newErrors.dobMonth = t.errRequired;
        if (!form.dobYear) newErrors.dobYear = t.errRequired;
        if (!form.contactNumber.trim()) newErrors.contactNumber = t.errRequired;
        if (!form.email.trim()) newErrors.email = t.errRequired;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) newErrors.email = t.errEmailInvalid;
        else if (!emailVerified) newErrors.email = t.errEmailVerify;
        if (!form.password.trim()) newErrors.password = t.errRequired;
        if (!form.confirmPassword.trim()) newErrors.confirmPassword = t.errRequired;
        if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
            newErrors.confirmPassword = t.errPasswordMatch;
        }
        if (!form.confirmToken.trim()) newErrors.confirmToken = t.errRequired;
        if (!form.termsAccepted) newErrors.termsAccepted = t.errAgreeTerms;
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error(t.errFillRequired);
            return;
        }

        const payload = {
            gender: getGenderLabel(form.gender),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            dob: `${form.dobYear}-${String(form.dobMonth).padStart(2, "0")}-01`,
            contactNumber: form.contactNumber ? `+${form.contactNumber}` : "",
            email: form.email.trim(),
            password: form.password,
            lang: t.langValue,
            receivePromotion: form.receivePromotion,
            confirmToken: form.confirmToken.trim(),
            existingMember: false,
        };

        setSubmitting(true);
        try {
            const res = await apiHelper.cineclub.createWalkInMember(payload);
            const hasBusinessError = res?.data?.code !== undefined && res.data.code !== 0;
            if (!isSuccessResponse(res) || hasBusinessError) {
                return;
            }
            toast.success(t.toastSuccess);
            handleReset();
        } catch (err) {
            // interceptor handles the toast; catch prevents unhandled rejection
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setForm(defaultForm);
        setErrors({});
        setOtpSent(false);
        setEmailVerified(false);
        setOtpCooldown(0);
    };

    return (
        <div className={classes.root}>
            <Box className={classes.header}>
                <PersonAddIcon style={{ fontSize: 32, color: "#306F9D" }} />
                <Typography variant="h4">{t.pageTitle}</Typography>
                <Box className={classes.langToggle}>
                    <Button
                        size="small"
                        variant={lang === "en" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => setLang("en")}
                        style={{ minWidth: "48px" }}
                    >
                        EN
                    </Button>
                    <Button
                        size="small"
                        variant={lang === "zhHant" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => setLang("zhHant")}
                        style={{ minWidth: "48px" }}
                    >
                        中文
                    </Button>
                </Box>
            </Box>

            <form onSubmit={handleSubmit}>
                <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>

                    {/* Plan & Payment */}
                    <Paper className={classes.card} elevation={1}>
                        <Typography className={classes.sectionTitle}>{t.sectionPlan}</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelPlan}</Typography>
                                <Box className={classes.planChip}>{t.planName}</Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelPayment}</Typography>
                                <Box className={classes.cashBadge}>{t.paymentMethod}</Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Personal Information */}
                    <Paper className={classes.card} elevation={1}>
                        <Typography className={classes.sectionTitle}>{t.sectionPersonal}</Typography>

                        {/* Prefix */}
                        <Box className={classes.fieldRow}>
                            <FormControl component="fieldset" error={!!errors.gender}>
                                <FormLabel component="legend" style={{ fontSize: "14px", marginBottom: "8px" }}>
                                    {t.labelPrefix} *
                                </FormLabel>
                                <RadioGroup row value={form.gender} onChange={handleChange("gender")}>
                                    {t.prefixOptions.map((opt) => (
                                        <FormControlLabel
                                            key={opt.value}
                                            value={opt.value}
                                            control={<Radio size="small" color="primary" />}
                                            label={<span style={{ fontSize: "14px" }}>{opt.label}</span>}
                                        />
                                    ))}
                                </RadioGroup>
                                {errors.gender && (
                                    <Typography style={{ fontSize: "12px", color: "#f44336" }}>{errors.gender}</Typography>
                                )}
                            </FormControl>
                        </Box>

                        {/* First Name + Last Name */}
                        <Grid container spacing={2} className={classes.fieldRow}>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelFirstName} *</Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder={t.placeholderFirstName}
                                    value={form.firstName}
                                    onChange={handleChange("firstName")}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelLastName} *</Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder={t.placeholderLastName}
                                    value={form.lastName}
                                    onChange={handleChange("lastName")}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                />
                            </Grid>
                        </Grid>

                        {/* Date of Birth + Mobile */}
                        <Grid container spacing={2} className={classes.fieldRow}>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelDob} *</Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <FormControl variant="outlined" size="small" fullWidth error={!!errors.dobMonth}>
                                            <InputLabel>{t.labelMonth}</InputLabel>
                                            <Select
                                                value={form.dobMonth}
                                                onChange={handleChange("dobMonth")}
                                                label={t.labelMonth}
                                            >
                                                {t.months.map((month, idx) => (
                                                    <MenuItem key={idx + 1} value={idx + 1}>{month}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {errors.dobMonth && (
                                            <Typography style={{ fontSize: "12px", color: "#f44336", marginTop: "4px" }}>{errors.dobMonth}</Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl variant="outlined" size="small" fullWidth error={!!errors.dobYear}>
                                            <InputLabel>{t.labelYear}</InputLabel>
                                            <Select
                                                value={form.dobYear}
                                                onChange={handleChange("dobYear")}
                                                label={t.labelYear}
                                            >
                                                {YEARS.map((year) => (
                                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {errors.dobYear && (
                                            <Typography style={{ fontSize: "12px", color: "#f44336", marginTop: "4px" }}>{errors.dobYear}</Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelMobile} *</Typography>
                                <Box className={classes.phoneWrapper}>
                                    <PhoneInput
                                        country="hk"
                                        value={form.contactNumber}
                                        onChange={(value) => {
                                            setForm((prev) => ({ ...prev, contactNumber: value }));
                                            if (errors.contactNumber) setErrors((prev) => ({ ...prev, contactNumber: "" }));
                                        }}
                                        preferredCountries={["hk", "cn", "us", "gb", "sg", "au", "tw", "jp", "kr", "my"]}
                                        countryCodeEditable={false}
                                        enableSearch
                                        inputStyle={{ width: "100%" }}
                                        disabled={disabled}
                                    />
                                </Box>
                                {errors.contactNumber && (
                                    <Typography style={{ fontSize: "12px", color: "#f44336", marginTop: "4px" }}>{errors.contactNumber}</Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Login Information */}
                    <Paper className={classes.card} elevation={1}>
                        <Typography className={classes.sectionTitle}>{t.sectionLogin}</Typography>

                        {/* Email + OTP on the same row */}
                        <Grid container spacing={2} className={classes.fieldRow}>
                            <Grid item xs={12} sm={7}>
                                <Typography className={classes.fieldLabel}>{t.labelEmail} *</Typography>
                                <Box display="flex" style={{ gap: "8px", alignItems: "flex-start" }}>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="email"
                                        placeholder={t.placeholderEmail}
                                        value={form.email}
                                        onChange={handleChange("email")}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        disabled={emailVerified}
                                    />
                                    <Button
                                        variant="contained"
                                        className={classes.sendCodeBtn}
                                        onClick={handleSendCode}
                                        disabled={sendingOtp || otpCooldown > 0 || emailVerified || !form.email.trim()}
                                    >
                                        {emailVerified
                                            ? t.btnSent
                                            : otpCooldown > 0
                                            ? t.btnResend(otpCooldown)
                                            : sendingOtp
                                            ? t.btnSending
                                            : t.btnSendCode}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <Typography className={classes.fieldLabel}>{t.labelOtp} *</Typography>
                                <Box display="flex" style={{ gap: "8px", alignItems: "flex-start" }}>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder={t.placeholderOtp}
                                        value={form.otpCode}
                                        onChange={handleChange("otpCode")}
                                        error={!!errors.otpCode}
                                        helperText={errors.otpCode}
                                        disabled={!otpSent || emailVerified}
                                        inputProps={{ maxLength: 6 }}
                                    />
                                    {emailVerified ? (
                                        <Box className={classes.verifiedChip} style={{ height: "40px", alignItems: "center", display: "flex" }}>
                                            <CheckCircleIcon style={{ fontSize: 18, color: "#2e7d32" }} />
                                            {t.emailVerified}
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            className={classes.verifyBtn}
                                            onClick={handleVerifyOtp}
                                            disabled={verifyingOtp || !otpSent || !form.otpCode.trim()}
                                        >
                                            {verifyingOtp ? t.btnVerifying : t.btnVerify}
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Password + Confirm Password */}
                        <Grid container spacing={2} className={classes.fieldRow}>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelPassword} *</Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    placeholder={t.placeholderPassword}
                                    value={form.password}
                                    onChange={handleChange("password")}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography className={classes.fieldLabel}>{t.labelConfirmPassword} *</Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    placeholder={t.placeholderConfirmPassword}
                                    value={form.confirmPassword}
                                    onChange={handleChange("confirmPassword")}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Terms & Conditions */}
                    <Paper className={classes.card} elevation={1}>
                        <Typography className={classes.sectionTitle}>{t.sectionTnc}</Typography>

                        <Box className={classes.termsBox}>
                            <div dangerouslySetInnerHTML={{ __html: tncText }} />
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.termsAccepted}
                                        onChange={handleChange("termsAccepted")}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={
                                    <span style={{ fontSize: "14px" }}>
                                        {t.labelAgree} *
                                    </span>
                                }
                            />
                            {errors.termsAccepted && (
                                <Typography style={{ fontSize: "12px", color: "#f44336", marginLeft: "32px" }}>
                                    {errors.termsAccepted}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!form.receivePromotion}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, receivePromotion: !e.target.checked }))
                                        }
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={
                                    <span style={{ fontSize: "14px" }}>
                                        {t.labelNoPromo}
                                    </span>
                                }
                            />
                        </Box>

                        <Box className={classes.fieldRow} style={{ marginTop: "16px" }}>
                            <Typography className={classes.fieldLabel}>{t.labelConfirmToken} *</Typography>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder={t.placeholderConfirmToken}
                                value={form.confirmToken}
                                onChange={handleChange("confirmToken")}
                                error={!!errors.confirmToken}
                                helperText={errors.confirmToken}
                            />
                        </Box>
                    </Paper>

                    {/* Actions */}
                    <Box display="flex" justifyContent="flex-end" style={{ gap: "12px" }}>
                        <Button variant="outlined" onClick={handleReset} disabled={disabled}>
                            {t.btnReset}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            className={classes.submitBtn}
                            disabled={disabled || submitting}
                        >
                            {submitting ? t.btnRegistering : t.btnRegister}
                        </Button>
                    </Box>

                </fieldset>
            </form>
        </div>
    );
}
